import { Box, Button, Checkbox, Flex, Menu, Popover, Select, Table, Text } from "@mantine/core";
import {
  CaretDownIcon,
  CaretUpDownIcon,
  CaretUpIcon,
  CreditCardIcon,
  EyeIcon,
  DownloadSimpleIcon,
  PlusIcon,
  XIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import qs from "qs";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSwr from "swr";
import { InvoiceApi } from "../../apis";
import { Page } from "../../components/layout/Page";
import {
  BulkActionBar,
  DateRangeChip,
  DueCell,
  ListFooter,
  ListPageHeader,
  ListStates,
  ListToolbar,
  MoneyCell,
  PartyCell,
  PaymentProgressCell,
  RowActions,
  StatCards,
  resolveListStates,
} from "../../components/list";
import sharedListClasses from "../../components/list/list.module.css";
import { usePermission } from "../../components/permission";
import { StatusBadge, resolveInvoiceStatusColor } from "../../components/ui/status-badge";
import { usePdfDownload } from "../../hooks/usePdfDownload";
import { useRowSelection } from "../../hooks/useRowSelection";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReference } from "../../interfaces/IReference";
import { currencyFormat } from "../../utils";
import { renderBusinessName } from "../../utils/render-business-name";
import { SELECT_ALL_CAP, saveInvoicePdfs } from "../../utils/bulk-invoice";
import { errorReason } from "../../utils/error-handler";
import { message } from "../../utils/Message";
import listClasses from "../sales-invoice/List.module.css";

// The received endpoint already drops DRAFT / RETURNED / CLOSED; CANCELED has no tab here.
const EXCLUDED_STATUSES = ["CANCELED", "CLOSED", "RETURNED", "DRAFT"];

type SortField = "confirmedDate" | "paymentDate" | "confirmedAmount" | "amountToPay";
type SortDir = "asc" | "desc";
type DueFilter = "overdue" | "week" | "none" | null;

/** Filters, sort and KPI summary all come from GET /inv/invoice/received (mirror of /invoice/sent). */
/** Money sums cover CONFIRMED invoices only, the same figures the supplier sees. */
type ReceivedSummary = {
  count: number;
  receivableCount?: number;
  confirmedAmount: number;
  paidAmount: number;
  amountToPay: number;
  overdueCount: number;
  overdueAmount: number;
};

type ListQuery = {
  status: string;
  query: string;
  senderBusinessId: string | null;
  confirmedFrom: string | null;
  confirmedTo: string | null;
  due: DueFilter;
  sortField: SortField | null;
  sortDir: SortDir;
  page: number;
  limit: number;
};

function parseDue(raw: string | null): DueFilter {
  return raw === "overdue" || raw === "week" || raw === "none" ? raw : null;
}

function parseSortField(raw: string | null): SortField | null {
  return raw === "confirmedDate" || raw === "paymentDate" || raw === "confirmedAmount" || raw === "amountToPay"
    ? raw
    : null;
}

function readQuery(params: URLSearchParams): ListQuery {
  return {
    status: params.get("status") || "",
    query: params.get("query") || "",
    senderBusinessId: params.get("partner") || null,
    confirmedFrom: params.get("from") || null,
    confirmedTo: params.get("to") || null,
    due: parseDue(params.get("due")),
    sortField: parseSortField(params.get("sort")),
    sortDir: params.get("dir") === "asc" ? "asc" : "desc",
    page: Math.max(1, parseInt(params.get("page") || "1", 10) || 1),
    limit: Math.max(1, parseInt(params.get("limit") || "20", 10) || 20),
  };
}

function writeQuery(q: ListQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.status) p.set("status", q.status);
  if (q.query) p.set("query", q.query);
  if (q.senderBusinessId) p.set("partner", q.senderBusinessId);
  if (q.confirmedFrom) p.set("from", q.confirmedFrom);
  if (q.confirmedTo) p.set("to", q.confirmedTo);
  if (q.due) p.set("due", q.due);
  if (q.sortField) {
    p.set("sort", q.sortField);
    p.set("dir", q.sortDir);
  }
  if (q.page !== 1) p.set("page", String(q.page));
  if (q.limit !== 20) p.set("limit", String(q.limit));
  return p;
}

/** Compact MNT for KPI cards (million / billion, 2 decimals); full value goes to the tooltip. */
function compactMnt(amount: number, t: (key: string) => string) {
  const billions = Math.abs(amount) >= 1_000_000_000;
  return {
    value: (amount / (billions ? 1_000_000_000 : 1_000_000)).toFixed(2),
    unit: t(billions ? "invoice:summaryStrip.unitBillion" : "invoice:summaryStrip.unitMillion"),
    full: currencyFormat(amount, { withSymbol: true }),
  };
}

/** Same rounding as the supplier KPI: never shows «100» or «0» for a part that is neither. */
function kpiPercent(part: number, whole: number): { ratio: number; pct: string } {
  if (!(whole > 0) || !(part > 0)) return { ratio: 0, pct: "0" };
  const raw = Math.min(100, (part / whole) * 100);
  if (raw < 1) return { ratio: raw, pct: (Math.floor(raw * 10) / 10).toFixed(1) };
  if (raw < 99.5) return { ratio: raw, pct: String(Math.round(raw)) };
  if (raw < 100) return { ratio: raw, pct: (Math.floor(raw * 10) / 10).toFixed(1) };
  return { ratio: 100, pct: "100" };
}

const rowTotal = (inv: any) => {
  const confirmed = Number(inv?.confirmedAmount || 0);
  return confirmed > 0 ? confirmed : Number(inv?.totalAmount || 0);
};
const rowPaid = (inv: any) => Number(inv?.paidAmount || 0);
const rowRemaining = (inv: any) => Number(inv?.amountToPay ?? Math.max(0, rowTotal(inv) - rowPaid(inv)));
const rowConfirmedDay = (inv: any): string | null => {
  const v = inv?.confirmedDate ?? inv?.sentDate;
  return v ? dayjs(v).format("YYYY-MM-DD") : null;
};

export const BuyerInvoiceList = () => {
  const { t } = useTranslation(["invoice", "common"]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [partnerOpen, setPartnerOpen] = useState(false);

  const invoiceStatus = useSelector((state: { general: IGeneral }) => state.general.invoiceStatus);

  const { download: downloadPdf, isLoading: isPdfLoading } = usePdfDownload({ fetcher: InvoiceApi.pdf });

  const INV_LIST = usePermission("INV_LIST");
  const INV_PDF = usePermission("INV_PDF");
  const INV_PAY_WITH_B2B = usePermission("INV_PAY_WITH_B2B");
  const INV_PAY_WITH_QPAY = usePermission("INV_PAY_WITH_QPAY");
  const hasPayAccess = INV_PAY_WITH_B2B.hasAccess || INV_PAY_WITH_QPAY.hasAccess;

  const q = useMemo(() => readQuery(searchParams), [searchParams]);

  const patchQuery = useCallback(
    (patch: Partial<ListQuery>, resetPage = true) => {
      setSearchParams(writeQuery({ ...q, ...patch, ...(resetPage ? { page: 1 } : {}) }), { replace: true });
    },
    [q, setSearchParams],
  );

  const offset = useMemo(() => ({ page: q.page, limit: q.limit }), [q.page, q.limit]);
  // Only filters that are set go out: the endpoint rejects keys it does not know, so an
  // older deploy without the date/due filters still serves the unfiltered list.
  const apiFilter = useMemo(
    () => ({
      query: q.query,
      status: q.status,
      senderBusinessId: q.senderBusinessId,
      ...(q.confirmedFrom ? { confirmedFrom: q.confirmedFrom } : {}),
      ...(q.confirmedTo ? { confirmedTo: q.confirmedTo } : {}),
      ...(q.due ? { due: q.due } : {}),
    }),
    [q.query, q.status, q.senderBusinessId, q.confirmedFrom, q.confirmedTo, q.due],
  );
  const sort = useMemo(
    () => (q.sortField ? { field: q.sortField, dir: q.sortDir } : null),
    [q.sortField, q.sortDir],
  );

  const swrKey = INV_LIST.hasAccess
    ? `inv.invoice.received?${qs.stringify({ offset, filter: apiFilter, sort })}`
    : null;

  const { data, error, isLoading, mutate } = useSwr<any>(
    swrKey,
    async () => await InvoiceApi.received({ offset, filter: apiFilter, sort } as any),
  );

  const rows: any[] = useMemo(() => (data?.rows as any[]) ?? [], [data]);
  const total = data?.count ?? 0;
  const summary: ReceivedSummary | null = data?.summary ?? null;

  const hasData = !!summary;
  const kpi = {
    invoiced: Number(summary?.confirmedAmount ?? 0),
    paid: Number(summary?.paidAmount ?? 0),
    remaining: Number(summary?.amountToPay ?? 0),
    overdueCount: Number(summary?.overdueCount ?? 0),
    overdueAmount: Number(summary?.overdueAmount ?? 0),
    count: Number(summary?.receivableCount ?? summary?.count ?? 0),
  };
  const kpiDash = t("common:list.dash");
  const kpiInvoiced = hasData ? compactMnt(kpi.invoiced, t) : null;
  const kpiPaid = hasData ? compactMnt(kpi.paid, t) : null;
  const kpiRemaining = hasData ? compactMnt(kpi.remaining, t) : null;
  const paidPct = kpiPercent(kpi.paid, kpi.invoiced);
  const remainingPct = kpiPercent(kpi.remaining, kpi.invoiced);
  const overduePct = kpiPercent(kpi.overdueAmount, kpi.remaining);

  // Supplier options come from the loaded rows (the received endpoint filters by senderBusinessId).
  const partnerOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const inv of rows) {
      const b = inv?.senderBusiness;
      if (b?.id && !map.has(b.id)) map.set(b.id, renderBusinessName(b, "-"));
    }
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [rows]);
  const partnerLabel = partnerOptions.find((p) => p.value === q.senderBusinessId)?.label ?? null;

  const hasChipFilters = !!(q.confirmedFrom || q.confirmedTo || q.senderBusinessId || q.due);
  const hasFilters = !!(q.query || q.status || hasChipFilters);

  const clearChipFilters = () =>
    patchQuery({ confirmedFrom: null, confirmedTo: null, senderBusinessId: null, due: null });

  const listStates = resolveListStates(
    <ListStates
      loading={isLoading}
      error={error}
      onRetry={() => mutate()}
      empty={rows.length === 0}
      filtered={hasFilters}
      query={q.query}
      onClear={() =>
        patchQuery({ query: "", status: "", confirmedFrom: null, confirmedTo: null, senderBusinessId: null, due: null })
      }
      columns={INV_PDF.hasAccess ? 8 : 7}
    />,
  );

  const statusLabel = useCallback(
    (code: string, fallback?: string) =>
      code === "SENT"
        ? t("invoice:buyerList.statusSent")
        : t(`invoice:invoiceStatus.${code}`, { defaultValue: fallback ?? code }),
    [t],
  );

  const statusTabs = useMemo(
    () => [
      { value: "", label: t("common:all"), count: summary?.count != null ? summary.count : total > 0 ? total : undefined },
      ...(invoiceStatus ?? [])
        .filter((s: IReference) => !EXCLUDED_STATUSES.includes(s.code))
        .map((s: IReference) => ({ value: s.code, label: statusLabel(s.code, s.name) })),
    ],
    [invoiceStatus, statusLabel, t, total, summary],
  );

  const onSort = (field: SortField) => {
    if (q.sortField === field) patchQuery({ sortDir: q.sortDir === "asc" ? "desc" : "asc" }, false);
    else patchQuery({ sortField: field, sortDir: "desc" }, false);
  };

  const sortIcon = (field: SortField) => {
    if (q.sortField !== field) return <CaretUpDownIcon size={11} opacity={0.35} />;
    return q.sortDir === "asc" ? <CaretUpIcon size={11} weight="bold" /> : <CaretDownIcon size={11} weight="bold" />;
  };

  // Selection survives paging; the selected rows are kept, so the bar total covers every page.
  const selection = useRowSelection<any>(rows);
  const { selectedRows, allPageSelected, somePageSelected } = selection;
  const [bulkBusy, setBulkBusy] = useState<"pdf" | "selectAll" | null>(null);
  const canSelect = INV_PDF.hasAccess;
  const selectedRemaining = useMemo(
    () => selectedRows.reduce((sum, r) => sum + rowRemaining(r), 0),
    [selectedRows],
  );
  const selectAllCount = Math.min(total, SELECT_ALL_CAP);
  const canSelectAll = allPageSelected && total > rows.length && selection.count < selectAllCount;

  const handleSelectAll = async () => {
    setBulkBusy("selectAll");
    try {
      const res: any = await InvoiceApi.received({
        offset: { page: 1, limit: selectAllCount },
        filter: apiFilter,
        sort,
      } as any);
      selection.select((res?.rows as any[]) ?? []);
    } catch (err) {
      message.error(errorReason(err));
    } finally {
      setBulkBusy(null);
    }
  };

  const handleBulkPdf = async () => {
    setBulkBusy("pdf");
    try {
      const failed = await saveInvoicePdfs(selectedRows);
      if (failed > 0) message.error(t("invoice:bulk.pdfFailed", { failed, count: selectedRows.length }));
    } finally {
      setBulkBusy(null);
    }
  };

  const goDetails = (id?: string) => {
    if (id) navigate(`/buyer-invoice/${id}/details`);
  };

  const partySubline = (inv: any) => {
    const ref = (inv?.refCode ?? "").toString().trim();
    const day = rowConfirmedDay(inv);
    const datePart = day ? day.slice(5) : "";
    if (ref && datePart) return `${ref} · ${datePart}`;
    return ref || datePart || null;
  };

  const dueLabel =
    q.due === "overdue"
      ? t("invoice:chips.dueOverdue")
      : q.due === "week"
        ? t("invoice:chips.dueWeek")
        : q.due === "none"
          ? t("invoice:chips.dueNone")
          : null;

  const supplierLabel = t("invoice:detail.party.supplier");

  return (
    <Page>
      <ListPageHeader
        eyebrow={t("invoice:breadcrumb.salesInvoice")}
        title={t("invoice:openInvoice")}
        search={{
          value: q.query,
          onChange: (v) => patchQuery({ query: v }),
          placeholder: t("invoice:searchPlaceholder"),
        }}
      />

      <StatCards
        items={[
          {
            key: "invoiced",
            label: t("invoice:summaryStrip.invoiced"),
            value: kpiInvoiced?.value ?? kpiDash,
            unit: kpiInvoiced?.unit,
            fullValue: kpiInvoiced?.full,
            tone: "gray",
            progress: hasData ? 100 : 0,
            hint: hasData ? t("invoice:summaryStrip.invoiceCount", { count: kpi.count }) : " ",
          },
          {
            key: "paid",
            label: t("invoice:summaryStrip.paid"),
            value: kpiPaid?.value ?? kpiDash,
            unit: kpiPaid?.unit,
            fullValue: kpiPaid?.full,
            tone: "green",
            progress: paidPct.ratio,
            hint: hasData ? t("invoice:summaryStrip.paidOfInvoiced", { pct: paidPct.pct }) : " ",
          },
          {
            key: "remaining",
            label: t("invoice:summaryStrip.remaining"),
            value: kpiRemaining?.value ?? kpiDash,
            unit: kpiRemaining?.unit,
            fullValue: kpiRemaining?.full,
            tone: "dark",
            progress: remainingPct.ratio,
            hint: hasData ? t("invoice:buyerList.remainingHint") : " ",
          },
          {
            key: "overdue",
            label: t("invoice:summaryStrip.overdue"),
            value: hasData ? kpi.overdueCount : kpiDash,
            unit: hasData ? t("invoice:summaryStrip.invoiceUnit") : undefined,
            tone: "red",
            progress: overduePct.ratio,
            hint: hasData ? currencyFormat(kpi.overdueAmount, { withSymbol: true }) : " ",
            onClick: () => patchQuery({ due: "overdue" }),
          },
        ]}
      />

      <Box className={listClasses.tableCard}>
        <ListToolbar
          tabs={statusTabs}
          activeTab={q.status}
          onTabChange={(value) => patchQuery({ status: value })}
          filterSlot={
            <>
              <DateRangeChip
                label={t("invoice:chips.confirmedDate")}
                value={[q.confirmedFrom, q.confirmedTo]}
                onChange={([confirmedFrom, confirmedTo]) => patchQuery({ confirmedFrom, confirmedTo })}
              />

              <Popover opened={partnerOpen} onChange={setPartnerOpen} position="bottom-end" shadow="md" withinPortal>
                <Popover.Target>
                  <Box
                    component="button"
                    type="button"
                    className={`${sharedListClasses.filterChip}${
                      q.senderBusinessId ? ` ${sharedListClasses.filterChipActive}` : ""
                    }`}
                    onClick={() => setPartnerOpen((o) => !o)}
                  >
                    {q.senderBusinessId ? (
                      <>
                        <Text span size="sm" fw={500}>
                          {partnerLabel ?? supplierLabel}
                        </Text>
                        <XIcon
                          size={12}
                          weight="bold"
                          onClick={(e) => {
                            e.stopPropagation();
                            patchQuery({ senderBusinessId: null });
                            setPartnerOpen(false);
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <PlusIcon size={12} weight="bold" />
                        <Text span size="sm">
                          {supplierLabel}
                        </Text>
                      </>
                    )}
                  </Box>
                </Popover.Target>
                <Popover.Dropdown>
                  <Select
                    searchable
                    clearable
                    nothingFoundMessage={t("common:noResultsShort")}
                    data={partnerOptions}
                    value={q.senderBusinessId}
                    onChange={(id) => {
                      patchQuery({ senderBusinessId: id });
                      setPartnerOpen(false);
                    }}
                    placeholder={supplierLabel}
                    w={280}
                    comboboxProps={{ withinPortal: false }}
                  />
                </Popover.Dropdown>
              </Popover>

              <Menu shadow="md" width={200} position="bottom-end" withinPortal>
                <Menu.Target>
                  <Box
                    component="button"
                    type="button"
                    className={`${sharedListClasses.filterChip}${dueLabel ? ` ${sharedListClasses.filterChipActive}` : ""}`}
                  >
                    {dueLabel ? (
                      <>
                        <Text span size="sm" fw={500}>
                          {dueLabel}
                        </Text>
                        <XIcon
                          size={12}
                          weight="bold"
                          onClick={(e) => {
                            e.stopPropagation();
                            patchQuery({ due: null });
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <PlusIcon size={12} weight="bold" />
                        <Text span size="sm">
                          {t("invoice:chips.due")}
                        </Text>
                      </>
                    )}
                  </Box>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => patchQuery({ due: "overdue" })}>{t("invoice:chips.dueOverdue")}</Menu.Item>
                  <Menu.Item onClick={() => patchQuery({ due: "week" })}>{t("invoice:chips.dueWeek")}</Menu.Item>
                  <Menu.Item onClick={() => patchQuery({ due: "none" })}>{t("invoice:chips.dueNone")}</Menu.Item>
                </Menu.Dropdown>
              </Menu>

              {hasChipFilters ? (
                <Button variant="subtle" color="gray" size="compact-sm" onClick={clearChipFilters}>
                  {t("common:list.clearFilters")}
                </Button>
              ) : null}
            </>
          }
        />

        {!INV_LIST.hasAccess ? (
          INV_LIST.accessDenied()
        ) : (
          <Box className={listClasses.tblWrap}>
            {listStates ?? (
              <Table.ScrollContainer minWidth={1000}>
                <Table className={listClasses.tbl} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
                  <Table.Thead>
                    <Table.Tr className={sharedListClasses.scrollRow}>
                      {canSelect && (
                        <Table.Th className={`${listClasses.colCheck} ${sharedListClasses.stickyCheck}`}>
                          <Checkbox
                            color="brand"
                            size="sm"
                            radius="sm"
                            checked={allPageSelected}
                            indeterminate={somePageSelected && !allPageSelected}
                            onChange={selection.toggleAll}
                            aria-label={t("common:all")}
                          />
                        </Table.Th>
                      )}
                      <Table.Th
                        className={`${listClasses.colActions} ${sharedListClasses.stickyActions} ${sharedListClasses.stickyEdge}`}
                      >
                        <Text className={sharedListClasses.stickyActionsHead}>{t("common:actions")}</Text>
                      </Table.Th>
                      <Table.Th className={listClasses.sortable} onClick={() => onSort("confirmedDate")}>
                        <Flex align="center" gap={4}>
                          {supplierLabel}
                          {sortIcon("confirmedDate")}
                        </Flex>
                      </Table.Th>
                      <Table.Th>{t("invoice:col.status")}</Table.Th>
                      <Table.Th className={listClasses.sortable} onClick={() => onSort("paymentDate")}>
                        <Flex align="center" gap={4}>
                          {t("invoice:col.dueDate")}
                          {sortIcon("paymentDate")}
                        </Flex>
                      </Table.Th>
                      <Table.Th
                        className={`${listClasses.colNum} ${listClasses.sortable}`}
                        onClick={() => onSort("confirmedAmount")}
                      >
                        <Flex align="center" gap={4} justify="flex-end">
                          {t("invoice:col.amount")}
                          {sortIcon("confirmedAmount")}
                        </Flex>
                      </Table.Th>
                      <Table.Th className={listClasses.colPayment}>{t("invoice:col.paymentProgress")}</Table.Th>
                      <Table.Th
                        className={`${listClasses.colNum} ${listClasses.sortable}`}
                        onClick={() => onSort("amountToPay")}
                      >
                        <Flex align="center" gap={4} justify="flex-end">
                          {t("invoice:summaryStrip.remaining")}
                          {sortIcon("amountToPay")}
                        </Flex>
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((inv: any, i: number) => {
                      const status = invoiceStatus.find((x: IReference) => x.code === inv.invoiceStatus);
                      const totalAmt = rowTotal(inv);
                      const paidAmt = rowPaid(inv);
                      const remainingAmt = rowRemaining(inv);
                      const isPaid = inv?.paymentStatus === "CLOSED" || (totalAmt > 0 && remainingAmt <= 0);
                      const canPay = inv.invoiceStatus === "CONFIRMED" && remainingAmt > 0 && hasPayAccess;

                      const selected = selection.isSelected(inv?.id);

                      return (
                        <Table.Tr
                          key={inv.id ?? inv.refCode ?? i}
                          className={sharedListClasses.scrollRow}
                          data-selected={selected || undefined}
                          onClick={() => goDetails(inv.id)}
                        >
                          {canSelect && (
                            <Table.Td
                              className={`${listClasses.colCheck} ${sharedListClasses.stickyCheck}`}
                              onClick={(e) => e.stopPropagation()}
                              // Shift-click selects a range; keep it from also selecting page text.
                              onMouseDown={(e) => e.shiftKey && e.preventDefault()}
                            >
                              <Checkbox
                                color="brand"
                                size="sm"
                                radius="sm"
                                checked={selected}
                                onChange={(e) => selection.toggle(i, (e.nativeEvent as MouseEvent).shiftKey)}
                              />
                            </Table.Td>
                          )}
                          <Table.Td
                            className={`${listClasses.colActions} ${sharedListClasses.stickyActions} ${sharedListClasses.stickyEdge}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <RowActions
                              inlineOnly
                              items={[
                                {
                                  key: "pay",
                                  intent: "pay",
                                  icon: <CreditCardIcon size={17} />,
                                  label: t("invoice:tooltip.pay"),
                                  onClick: () => navigate(`/buyer-invoice/${inv.id}/pay`),
                                  hidden: !canPay,
                                },
                                {
                                  key: "review",
                                  intent: "view",
                                  icon: <EyeIcon size={17} />,
                                  label: t("invoice:tooltip.review"),
                                  onClick: () => navigate(`/buyer-invoice/${inv.id}/review`),
                                  hidden: inv.invoiceStatus !== "SENT",
                                },
                                {
                                  key: "pdf",
                                  intent: "pdf",
                                  icon: <DownloadSimpleIcon size={17} />,
                                  label: t("invoice:tooltip.pdf"),
                                  onClick: () => downloadPdf(`${inv.id}`),
                                  loading: isPdfLoading(inv.id),
                                  disabled: !INV_PDF.hasAccess,
                                  disabledReason: INV_PDF.reason,
                                },
                              ]}
                            />
                          </Table.Td>
                          <Table.Td>
                            <PartyCell
                              name={renderBusinessName(inv?.senderBusiness, "-")}
                              logo={inv?.senderBusiness?.logo}
                              code={partySubline(inv)}
                              codeHref={inv?.id ? `/buyer-invoice/${inv.id}/details` : null}
                            />
                          </Table.Td>
                          <Table.Td>
                            <StatusBadge
                              color={resolveInvoiceStatusColor(inv.invoiceStatus, status?.color)}
                              label={statusLabel(inv.invoiceStatus, status?.name)}
                            />
                          </Table.Td>
                          <Table.Td>
                            <DueCell dueDate={inv?.paymentDate} paid={isPaid || inv.invoiceStatus !== "CONFIRMED"} />
                          </Table.Td>
                          <Table.Td className={listClasses.colNum}>
                            <MoneyCell amount={totalAmt > 0 ? totalAmt : null} />
                          </Table.Td>
                          <Table.Td className={listClasses.colPayment}>
                            <PaymentProgressCell paid={paidAmt} total={totalAmt} />
                          </Table.Td>
                          <Table.Td className={listClasses.colNum}>
                            <MoneyCell amount={remainingAmt > 0 ? remainingAmt : null} emphasis />
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Box>
        )}

        <ListFooter
          from={Math.min(total, (q.page - 1) * q.limit + 1)}
          to={Math.min(total, (q.page - 1) * q.limit + rows.length)}
          total={total}
          page={q.page}
          pageSize={q.limit}
          onPageChange={(p) => patchQuery({ page: p }, false)}
          onPageSizeChange={(limit) => patchQuery({ page: 1, limit })}
        />
      </Box>

      <BulkActionBar
        selectedCount={selection.count}
        remainingTotal={selectedRemaining}
        onClear={selection.clear}
        selectAllAction={
          canSelectAll
            ? {
                label: t(total > SELECT_ALL_CAP ? "common:list.bulkSelectFirst" : "common:list.bulkSelectAll", {
                  count: selectAllCount,
                }),
                onClick: handleSelectAll,
                loading: bulkBusy === "selectAll",
              }
            : undefined
        }
        primaryAction={{
          label: t("invoice:actions.downloadPdf"),
          onClick: handleBulkPdf,
          loading: bulkBusy === "pdf",
        }}
      />
    </Page>
  );
};
